import clsx from 'clsx'
import { Icon } from 'components/ui/Icon'
import { CitationBadge } from 'components/ui/CitationBadge'
import { ToolSourceRow } from 'components/messages/ToolSourceRow'
import { CitationSource } from 'types/chat-message'

type SourceRowProps = {
  sourceKey: string
  source: CitationSource
}

const ROW_CLASSES = 'tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-truncate'

export const SourceRow = ({ sourceKey, source }: SourceRowProps) => {
  if (source.type === 'qa') {
    return (
      <span className={ROW_CLASSES} style={{ color: 'var(--text-muted)' }}>
        <CitationBadge label={sourceKey} variant="neutral" size="sm" />
        <Icon
          icon={['fas', 'comment-dots']}
          className="tw-w-3 tw-h-3 tw-flex-shrink-0"
          style={{ color: 'var(--text-tertiary)' }}
        />
        <span className="tw-truncate">{source.name}</span>
      </span>
    )
  }

  // Agentic tool call: not a document, never a link. Expands to show its args (e.g. the
  // Qlik query behind the data) so the user can inspect/reproduce them.
  if (source.type === 'tool') {
    return <ToolSourceRow sourceKey={sourceKey} source={source} />
  }

  if (source.type === 'attachment') {
    const content = (
      <>
        <CitationBadge label={sourceKey} variant="neutral" size="sm" />
        <Icon
          icon={['fas', 'paperclip']}
          className="tw-w-3 tw-h-3 tw-flex-shrink-0"
          style={{ color: 'var(--text-tertiary)' }}
        />
        <span className="tw-truncate">{source.name}</span>
      </>
    )
    return source.url ? (
      <a
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(ROW_CLASSES, 'tw-text-[var(--text-secondary)] hover:tw-text-brand hover:tw-underline')}
      >
        {content}
      </a>
    ) : (
      <span className={ROW_CLASSES} style={{ color: 'var(--text-muted)' }}>
        {content}
      </span>
    )
  }

  const isSite = source.type === 'site'
  const href = isSite
    ? (source.pageUrl ?? source.url ?? '')
    : source.url
      ? source.url + (source.page != null ? `#page=${source.page}` : '')
      : ''

  const content = (
    <>
      <CitationBadge label={sourceKey} size="sm" />
      <Icon
        icon={['fas', isSite ? 'globe' : 'file-pdf']}
        className="tw-w-3 tw-h-3 tw-flex-shrink-0"
        style={{ color: 'var(--brand-text)' }}
      />
      <span className="tw-truncate">
        {source.name}
        {!isSite && source.page != null && ` (p. ${source.page})`}
      </span>
    </>
  )

  // A source without a url must never be an anchor (an empty href reloads the page).
  if (!href) {
    return (
      <span className={ROW_CLASSES} style={{ color: 'var(--text-muted)' }}>
        {content}
      </span>
    )
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={clsx(
        ROW_CLASSES,
        'tw-min-h-[24px] tw-py-1 tw-text-[var(--text-secondary)] hover:tw-text-brand hover:tw-underline tw-transition-colors',
      )}
    >
      {content}
    </a>
  )
}
